import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import CommonLoader from '../../../components/common-loader/CommonLoader';
import { SONAR_PROJECT_RANGE_TYPE } from '../../../constants';
import { editCreateSonarProject, initSonarProject } from '../../../redux/create-sonar/createSonarActions';
import { resetCreateSonarData } from '../../../redux/create-sonar/createSonarReducer';
import dayjs, { DATE_FORMATS } from '../../../utils/dateUtils';
import getMaxSonarDate from '../../../utils/getMaxSonarDate';
import validate from '../../../utils/validate';
import { createSonarProjectYopValidation } from '../validation';
import SonarCreationForm from './sonar-creation-form/SonarCreationForm';

const SonarCreation = () => {
  const dispatch = useDispatch();

  const projectInfo = useSelector((state) => state.createSonar.projectInfo);
  const initLoading = useSelector((state) => state.createSonar.initLoading);

  const { month, year } = useMemo(() => {
    const startDate = projectInfo.startDate;

    if (!startDate && !dayjs(startDate, DATE_FORMATS.API_DATE).isValid()) {
      const maxDate = getMaxSonarDate();

      return {
        month: maxDate.month,
        year: maxDate.year,
      };
    }

    const checkDate = dayjs(startDate, DATE_FORMATS.API_DATE);

    return {
      month: checkDate.format('MM'),
      year: checkDate.format('YYYY'),
    };
  }, [projectInfo.startDate]);

  const createSonarProjectInitialValues = useMemo(
    () => ({
      projectName: projectInfo.projectName || '',
      brand: projectInfo.brand || '',
      startingFromMonth: month || '',
      startingFromYear: year || '',
      activeUsers: projectInfo.users || [],
      assignUser: '',
      rangeType: projectInfo.rangeType ?? SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE,
    }),
    [projectInfo.projectName, projectInfo.users, projectInfo.rangeType, projectInfo.brand, month, year],
  );

  const handleSubmit = useCallback(
    async (values) => {
      await dispatch(editCreateSonarProject({ ...values, formed: true }));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(initSonarProject());

    return () => {
      dispatch(resetCreateSonarData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Form
        initialValues={createSonarProjectInitialValues}
        onSubmit={handleSubmit}
        validate={(values) => validate(values, createSonarProjectYopValidation())}
        subscription={{ submitting: true, pristine: true }}
        render={({ handleSubmit }) => <SonarCreationForm handleSubmit={handleSubmit} />}
      />

      <CommonLoader isLoading={initLoading} />
    </>
  );
};

export default memo(SonarCreation);
